import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi'
import { z } from 'zod'
import { OverrideIconTypeSchema, VisibleIfSchema } from './BaseFormQuestionAndAnswerSchemas'

extendZodWithOpenApi(z)

/**
 * Per-instance fields shared by every variant — what the Form Template's per-question entry
 * in FormConfig carries regardless of question type.
 *
 * TODO (Question Syncing): this should grow as we identify more fields that every question type
 * needs to define at the instance level.
 */
export const BaseQuestionInstanceSchema = z.object({
  id: z.string(),
  visibleIf: VisibleIfSchema.optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  isRequired: z.boolean().optional(),
  readOnly: z.boolean().optional(),
})
export type BaseQuestionInstance = z.infer<typeof BaseQuestionInstanceSchema>

/**
 * Fields **intrinsic** to a Question: properties of the Question itself that should
 * be consistent across all instances. Persisted on `question_versions.config`.
 *
 * TODO (Question Syncing): this should shrink as we identify which fields are truly intrinsic
 * and should be the same across all instances.
 */
export const BaseQuestionDefinitionSchema = z.object({
  name: z.string(),
  disabled: z.boolean().optional(),
  weighting: z.number().optional(),
  tooltip: z.string().optional(),
  doubleEntryEnabled: z.boolean().optional(),
  overrideIconType: OverrideIconTypeSchema.optional(),
  isScorable: z.boolean().optional(),
  autoFlaggedScoreCategoryIds: z.array(z.string().uuid()).optional(),
  isMaterial: z.boolean().optional(),
})
export type BaseQuestionDefinition = z.infer<typeof BaseQuestionDefinitionSchema>

/**
 * Fields that are **overrideable** per Form Template instance. Persisted on
 * `question_versions.config` as a default (required here), and mirrored on
 * `BaseQuestionInstanceSchema` as optional override slots.
 *
 * TODO (Question Syncing): this should grow as we identify more fields that are instance related,
 * but can have an overrideable default value.
 */
export const BaseQuestionConfigSchema = z.object({
  visibleIf: VisibleIfSchema,
  title: z.string(),
  description: z.string().optional(),
  isRequired: z.boolean(),
  readOnly: z.boolean().optional(),
})

export type BaseQuestionConfig = z.infer<typeof BaseQuestionConfigSchema>

/**
 * Overrideable-default fields made optional on the *stored* Question config — a Question may omit a default and
 * let each Form instance supply it (the resolved `FormQuestion` still requires them; see `QuestionConfigSchema`).
 */
export const OVERRIDEABLE_DEFAULT_FIELD_MASK = {
  visibleIf: true,
  title: true,
  description: true,
  isRequired: true,
  readOnly: true,
} as const satisfies Record<keyof BaseQuestionConfig, true>

/**
 * Stored link from a Form Template's per-question entry to an Organisation Question. Carries the
 * Question `id` only — the Version the Form Template tracks (and whether it is pinned) lives in the
 * `form_templates_question_versions` link table, never in the `form_templates.config` column.
 */
export const OrganisationQuestionLinkSchema = z.object({
  id: z.string().uuid(),
})
export type OrganisationQuestionLink = z.infer<typeof OrganisationQuestionLinkSchema>

/**
 * A resolved Organisation Question link: the stored `{ id }` enriched with the Version the Form
 * Template renders and whether that Version is pinned — both sourced from the
 * `form_templates_question_versions` link table at resolve time.
 */
export const ResolvedOrganisationQuestionLinkSchema = OrganisationQuestionLinkSchema.extend({
  versionId: z.number().int().positive(),
  isQuestionVersionPinned: z.boolean(),
})
export type ResolvedOrganisationQuestionLink = z.infer<typeof ResolvedOrganisationQuestionLinkSchema>

/**
 * Storage shape of a Linked entry on a Form Template's per-question array — carries the
 * per-instance fields plus the link payload. No `type`, no variant-specific fields; those are
 * resolved at render time from the linked Question's Definition + Config.
 */
export const LinkedFormQuestionSchema = BaseQuestionInstanceSchema.extend({
  organisationQuestion: OrganisationQuestionLinkSchema,
}).strict()
export type LinkedFormQuestion = z.infer<typeof LinkedFormQuestionSchema>
